import { useEffect, useState, useCallback } from "react";
import { useNavigate, NavLink } from "react-router-dom";
import Logo from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import AdminSendResetButton from "@/components/AdminSendResetButton";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  LayoutDashboard, Ticket, Users, Coins, Wallet, LifeBuoy,
  UserCheck, LogOut, Settings, ShieldCheck, ClipboardList, ScrollText,
  Menu, X, MoreVertical, Box, Bell, BellRing, Megaphone, Network,
} from "lucide-react";

const userNav = [
  { to: "/app", end: true, label: "Overview", icon: LayoutDashboard, testid: "nav-overview" },
  { to: "/app/buy", label: "Buy Coupon", icon: Ticket, testid: "nav-buy" },
  { to: "/app/coupons", label: "My Coupons", icon: Ticket, testid: "nav-coupons" },
  { to: "/app/referrals", label: "Referrals", icon: Users, testid: "nav-referrals" },
  { to: "/app/commissions", label: "Commissions", icon: Coins, testid: "nav-commissions" },
  { to: "/app/withdrawals", label: "Withdrawals", icon: Wallet, testid: "nav-withdrawals" },
  { to: "/app/kyc", label: "KYC", icon: UserCheck, testid: "nav-kyc" },
  { to: "/app/boombox", label: "Boom Box", icon: Box, testid: "nav-boombox" },
  { to: "/app/support", label: "Support", icon: LifeBuoy, testid: "nav-support" },
];

const adminNav = [
  { to: "/admin", end: true, label: "Overview", icon: LayoutDashboard, testid: "nav-a-overview" },
  { to: "/admin/users", label: "Users", icon: Users, testid: "nav-a-users" },
  { to: "/admin/mlm", label: "MLM Network", icon: Network, testid: "nav-a-mlm" },
  { to: "/admin/orders", label: "Orders", icon: ClipboardList, testid: "nav-a-orders" },
  { to: "/admin/commissions", label: "Commissions", icon: Coins, testid: "nav-a-commissions" },
  { to: "/admin/withdrawals", label: "Withdrawals", icon: Wallet, testid: "nav-a-withdrawals" },
  { to: "/admin/kyc", label: "KYC", icon: ShieldCheck, testid: "nav-a-kyc" },
  { to: "/admin/tickets", label: "Support", icon: LifeBuoy, testid: "nav-a-tickets" },
  { to: "/admin/boombox", label: "Boom Box", icon: Box, testid: "nav-a-boombox" },
  { to: "/admin/notifications", label: "Notifications", icon: Megaphone, testid: "nav-a-notifications" },
  { to: "/admin/settings", label: "Settings", icon: Settings, testid: "nav-a-settings" },
  { to: "/admin/audit", label: "Audit Logs", icon: ScrollText, testid: "nav-a-audit" },
];

const COLLAPSED_KEY = "cc_sidebar_collapsed";

function NotificationBell({ testid = "notification-bell", className = "" }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items || []);
      setUnread(data.unread_count || 0);
    } catch (e) { /* silent — badge just stays as-is */ }
    finally { setLoading(false); }
  }, []);

  const loadCount = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/count");
      setUnread(data.unread_count || 0);
    } catch (e) { /* silent */ }
  }, []);

  // Poll unread count every 45s in the background so the badge stays fresh
  // without hammering the API. Full list is fetched when the panel opens.
  useEffect(() => {
    loadCount();
    const t = setInterval(loadCount, 45000);
    return () => clearInterval(t);
  }, [loadCount]);

  useEffect(() => { if (open) load(); }, [open, load]);

  // Close on outside click / Escape
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (!e.target.closest?.("[data-testid='notification-panel']") &&
          !e.target.closest?.(`[data-testid='${testid}']`)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
  }, [open, testid]);

  const markRead = async (id) => {
    try {
      await api.post(`/notifications/${id}/read`);
      setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
      setUnread((u) => Math.max(0, u - 1));
    } catch (e) { /* ignore */ }
  };
  const markAll = async () => {
    try {
      await api.post("/notifications/read-all");
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnread(0);
      toast.success("All notifications marked as read");
    } catch (e) { /* ignore */ }
  };

  return (
    <div className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={testid}
        aria-label="Notifications"
        className="relative h-9 w-9 inline-flex items-center justify-center rounded-md text-slate-200 hover:text-white hover:bg-white/10"
      >
        {unread > 0 ? <BellRing className="h-4 w-4" /> : <Bell className="h-4 w-4" />}
        {unread > 0 && (
          <span
            data-testid="notification-unread-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-[#D4AF37] text-black text-[10px] font-bold flex items-center justify-center tabular"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notification-panel"
          className="absolute right-0 mt-2 w-[92vw] max-w-sm rounded-md border border-white/10 bg-[#17181B] shadow-2xl z-50"
        >
          <div className="flex items-center justify-between p-3 border-b border-white/10">
            <div className="text-sm font-medium">Notifications</div>
            <button
              onClick={markAll}
              disabled={unread === 0}
              data-testid="btn-mark-all-read"
              className="text-xs text-slate-300 hover:text-white disabled:opacity-40"
            >
              Mark all as read
            </button>
          </div>
          <div className="max-h-[60vh] overflow-y-auto">
            {loading && items.length === 0 && <div className="p-4 text-xs text-slate-400">Loading…</div>}
            {!loading && items.length === 0 && <div className="p-4 text-xs text-slate-400" data-testid="notif-empty">No notifications yet.</div>}
            {items.map((n) => (
              <button
                key={n.id}
                onClick={() => !n.read && markRead(n.id)}
                data-testid={`notif-${n.id}`}
                className={`w-full text-left block p-3 border-b border-white/5 hover:bg-white/[0.03] transition ${!n.read ? "bg-[#D4AF37]/[0.06]" : ""}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="text-sm font-medium text-white">{n.title}</div>
                  {!n.read && <span className="mt-1 shrink-0 h-2 w-2 rounded-full bg-[#D4AF37]" data-testid="notif-unread-dot" />}
                </div>
                <div className="text-xs text-slate-300 mt-1 whitespace-pre-line">{n.message}</div>
                <div className="text-[10px] text-slate-500 mt-2">{new Date(n.created_at).toLocaleString()}</div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


function SidebarLink({ item, collapsed, onClick }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={onClick}
      data-testid={item.testid}
      title={collapsed ? item.label : undefined}
      className={({ isActive }) =>
        `sidebar-link ${isActive ? "active" : ""} ${collapsed ? "collapsed" : ""}`
      }
    >
      <Icon className="h-4 w-4 shrink-0" />
      {!collapsed && <span className="truncate">{item.label}</span>}
    </NavLink>
  );
}

export default function DashboardShell({ children, isAdmin = false }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const items = isAdmin ? adminNav : userNav;

  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(COLLAPSED_KEY) === "1"; } catch { return false; }
  });
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    try { localStorage.setItem(COLLAPSED_KEY, collapsed ? "1" : "0"); } catch { /* noop */ }
  }, [collapsed]);

  // Lock body scroll when mobile drawer is open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const doLogout = async () => { await logout(); nav("/"); };
  const asideWidth = collapsed ? "lg:w-16" : "lg:w-64";

  // User panel is themed Olive Green + Light Mocha + Gold; admin
  // keeps the existing charcoal/black theme. All theme rules live under
  // [data-app-theme="user-olive"] in index.css so nothing else changes.
  const rootThemeAttr = isAdmin ? undefined : "user-olive";
  const pageBg = isAdmin ? "bg-[#0A0A0A]" : "bg-[#E8DCC9]";
  const sidebarBg = isAdmin ? "bg-[#17181B]" : "bg-[#35421F]";  // dark olive rail for users
  const sidebarBorder = isAdmin ? "border-white/5" : "border-[#1D2415]";
  const mobileHeaderBg = sidebarBg;
  const mobileHeaderBorder = sidebarBorder;
  const desktopHeaderBg = isAdmin ? "bg-[#0A0A0A]/80" : "bg-[#E8DCC9]/85";

  const SidebarInner = ({ isCollapsed, onNav }) => (
    <>
      <div className={`flex items-center ${isCollapsed ? "justify-center" : "justify-between"} mb-6`}>
        {!isCollapsed && <Logo tone="light" />}
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          className="hidden lg:inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-300 hover:text-white hover:bg-white/5"
          aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          data-testid="sidebar-toggle"
        >
          <MoreVertical className="h-4 w-4" />
        </button>
      </div>

      <nav className="flex-1 space-y-1 overflow-y-auto" data-testid="side-nav">
        {items.map((it) => (
          <SidebarLink key={it.to} item={it} collapsed={isCollapsed} onClick={onNav} />
        ))}
      </nav>

      <div className={`pt-4 mt-4 border-t border-white/10 ${isCollapsed ? "text-center" : ""}`}>
        {!isCollapsed ? (
          <>
            <div className="text-xs text-slate-400">Signed in as</div>
            <div className="text-sm font-medium truncate">{user?.email}</div>
            <div className="text-xs text-slate-400 mt-1">{isAdmin ? "Administrator" : "Member"}</div>
            {isAdmin && (
              <div className="mt-3">
                <AdminSendResetButton variant="compact" />
              </div>
            )}
            <Button
              variant="ghost"
              onClick={doLogout}
              className="mt-3 w-full justify-start text-slate-200 hover:text-white hover:bg-white/10"
              data-testid="btn-logout-side"
            >
              <LogOut className="h-4 w-4 mr-2" /> Logout
            </Button>
          </>
        ) : (
          <button
            type="button"
            onClick={doLogout}
            title="Logout"
            className="w-full inline-flex items-center justify-center h-9 rounded-md text-slate-200 hover:text-white hover:bg-white/10"
            data-testid="btn-logout-side"
          >
            <LogOut className="h-4 w-4" />
          </button>
        )}
      </div>
    </>
  );

  return (
    <div className={`min-h-screen w-full ${pageBg} flex`} data-app-theme={rootThemeAttr}>
      {/* Desktop sidebar */}
      <aside
        className={`hidden lg:flex ${asideWidth} shrink-0 flex-col ${sidebarBg} text-white p-4 sticky top-0 h-screen border-r ${sidebarBorder} transition-[width] duration-200`}
        data-testid="admin-sidebar"
      >
        <SidebarInner isCollapsed={collapsed} />
      </aside>

      {/* Mobile drawer + overlay */}
      {mobileOpen && (
        <>
          <div
            className="lg:hidden fixed inset-0 z-40 bg-black/60"
            onClick={() => setMobileOpen(false)}
            data-testid="mobile-drawer-overlay"
          />
          <aside
            className={`lg:hidden fixed top-0 left-0 z-50 h-full w-[78%] max-w-xs ${sidebarBg} text-white p-4 flex flex-col border-r ${sidebarBorder} shadow-2xl`}
            data-testid="mobile-drawer"
          >
            <div className="flex items-center justify-between mb-6">
              <Logo tone="light" />
              <button
                type="button"
                onClick={() => setMobileOpen(false)}
                className="h-8 w-8 inline-flex items-center justify-center rounded-md text-slate-300 hover:text-white hover:bg-white/5"
                aria-label="Close menu"
                data-testid="mobile-drawer-close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="flex-1 space-y-1 overflow-y-auto" data-testid="side-nav-mobile">
              {items.map((it) => (
                <SidebarLink
                  key={it.to}
                  item={it}
                  collapsed={false}
                  onClick={() => setMobileOpen(false)}
                />
              ))}
            </nav>
            <div className="pt-4 mt-4 border-t border-white/10">
              <div className="text-xs text-slate-400">Signed in as</div>
              <div className="text-sm font-medium truncate">{user?.email}</div>
              <div className="text-xs text-slate-400 mt-1">{isAdmin ? "Administrator" : "Member"}</div>
              {isAdmin && (
                <div className="mt-3">
                  <AdminSendResetButton variant="compact" />
                </div>
              )}
              <Button
                variant="ghost"
                onClick={doLogout}
                className="mt-3 w-full justify-start text-slate-200 hover:text-white hover:bg-white/10"
                data-testid="btn-logout-mobile-drawer"
              >
                <LogOut className="h-4 w-4 mr-2" /> Logout
              </Button>
            </div>
          </aside>
        </>
      )}

      {/* Main column */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Mobile top bar with hamburger */}
        <header className={`lg:hidden sticky top-0 z-30 ${mobileHeaderBg} text-white px-4 py-3 flex items-center justify-between border-b ${mobileHeaderBorder}`}>
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            className="h-9 w-9 inline-flex items-center justify-center rounded-md text-slate-200 hover:text-white hover:bg-white/10"
            aria-label="Open menu"
            data-testid="mobile-menu-toggle"
          >
            <Menu className="h-5 w-5" />
          </button>
          <Logo tone="light" />
          <div className="flex items-center gap-1">
            <NotificationBell testid="notification-bell-mobile" />
            <button
              type="button"
              onClick={doLogout}
              className="h-9 w-9 inline-flex items-center justify-center rounded-md text-slate-200 hover:text-white hover:bg-white/10"
              aria-label="Logout"
              data-testid="btn-logout-mobile"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </header>

        {/* Desktop notification bar — bell in the top-right, above the content */}
        <div className={`hidden lg:flex items-center justify-end sticky top-0 z-20 ${desktopHeaderBg} backdrop-blur px-6 lg:px-10 pt-4`}>
          <NotificationBell testid="notification-bell-desktop" />
        </div>

        <main className="p-4 sm:p-6 lg:p-10 lg:pt-4 max-w-7xl mx-auto w-full">{children}</main>
      </div>
    </div>
  );
}
